import { useMemo } from 'react';
import {
  ExitToApp,
  AccountCircle,
  PeopleAltOutlined,
  AccountTreeOutlined,
  QuestionAnswerOutlined,
  AddIcCall,
  AccountBalance,
  MenuBookOutlined,
} from '@mui/icons-material';
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import SchemaIcon from '@mui/icons-material/Schema';
import SettingsPhoneIcon from '@mui/icons-material/SettingsPhone';

import {
  SipTrunkPage,
  OrganizationPage,
  RolesAndPermissionsPage,
  UsersAndOrganizationsPage,
  KnowledgeManagementPage,
  SchemaEditorPage,
  CallManagementPage,
  ModelsManagementPage,
  KnowledgeSourceList,
  BatchCallListPage,
  CallTaskWizardPage,
  CallsManagementPage,
  PromptLibraryPage,
  AnalyticsPage,
  SipTrunkVendorsPage,
} from '../../pages';
import { MenuLabel, ROUTE_PATTERN, ROUTE_PATH } from './const';
import type { MenuItem } from './Layout';
import {
  AgentsOrgScopedRoute,
  NewAgentOrgScopedRoute,
  AgentOrgScopedRoute,
  ConversationsOrgScopedRoute,
  ConversationDetailOrgScopedRoute,
  AgentsBillingOrgScopedRoute,
  ConversationsBillingOrgScopedRoute,
  ConversationBillingDetailOrgScopedRoute,
} from './orgScopedRoutes';
import {
  AnalyticsIcon,
  BatchCallIcon,
  FolderIcon,
  RetryCallIcon,
  UserGroupIcon,
} from '../../assets/icons';
import { PERMISSIONS } from '../../shared/constants/permissions';
import LiveKitServerPage from '../../pages/livekit-server';

export const getAuthenticatedMenuItems = (logout: Function): MenuItem[] => [
  {
    menuTitle: MenuLabel.Analytics,
    pageTitle: 'Analytics',

    url: ROUTE_PATH.ANALYTICS,
    icon: <AnalyticsIcon />,
    component: AnalyticsPage,
    action: [
      PERMISSIONS.VIEW_DASHBOARD_ANALYTICS,
      PERMISSIONS.VIEW_WIDGET_ANALYTICS,
    ],
  },
  {
    menuTitle: MenuLabel.ManageAgents,
    pageTitle: 'Agents List',

    url: ROUTE_PATTERN.agents.list,
    icon: <PeopleAltOutlined />,
    component: AgentsOrgScopedRoute,
    action: PERMISSIONS.VIEW_AGENTS,
  },
  {
    menuTitle: MenuLabel.Conversations,
    pageTitle: 'Conversations List',

    url: ROUTE_PATTERN.conversation.list,
    icon: <QuestionAnswerOutlined />,
    component: ConversationsOrgScopedRoute,
    action: PERMISSIONS.VIEW_CONVERSATIONS,
  },
  {
    pageTitle: 'Conversation',

    url: ROUTE_PATTERN.conversation.detail,
    component: ConversationDetailOrgScopedRoute,
    action: PERMISSIONS.VIEW_CONVERSATIONS,
  },
  {
    pageTitle: 'Create new Agent',

    url: ROUTE_PATTERN.agents.new,
    component: NewAgentOrgScopedRoute,
    action: PERMISSIONS.CREATE_AGENTS,
  },
  {
    pageTitle: 'Agent Configuration',

    url: ROUTE_PATTERN.agents.detail,
    icon: <AccountCircle />,
    component: AgentOrgScopedRoute,
    action: PERMISSIONS.VIEW_AGENTS,
  },
  {
    menuTitle: MenuLabel.KnowledgeManagement,
    pageTitle: 'Knowledge Management',

    url: '/knowledge_management',
    icon: <FolderIcon />,
    component: KnowledgeManagementPage,
    action: [PERMISSIONS.MANAGE_KNOWLEDGE, PERMISSIONS.MANAGE_SCRAPER],
  },
  {
    pageTitle: 'Knowledge Details',

    url: '/knowledge_management/:id',
    component: KnowledgeSourceList,
    action: PERMISSIONS.MANAGE_KNOWLEDGE,
  },
  {
    pageTitle: 'Organization',

    url: '/org_management/organization/:id',
    component: OrganizationPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.CallManagement,
    pageTitle: 'Call Management',

    url: '/call_management',
    component: CallManagementPage,
    icon: <RetryCallIcon />,
    action: [PERMISSIONS.MAKE_CALL, PERMISSIONS.MAKE_CONFERENCE_CALL],
  },
  {
    menuTitle: MenuLabel.RoleManagement,

    url: '/role_management',
    icon: <AccountTreeOutlined />,
    component: RolesAndPermissionsPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.OrgManagement,

    url: '/org_management',
    icon: <UserGroupIcon />,
    component: UsersAndOrganizationsPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.JSONManagement,

    url: '/json_management',
    component: SchemaEditorPage,
    icon: <SchemaIcon />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.ModelsManagement,

    url: '/models_management',
    component: ModelsManagementPage,
    icon: <DomainAddIcon />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.SipTrunk,

    url: '/sip_trunk',
    icon: <AddIcCall />,
    component: SipTrunkPage,
    action: PERMISSIONS.MANAGE_SIP_TRUNK,
  },
  {
    menuTitle: MenuLabel.ConversationsBilling,

    url: ROUTE_PATTERN.billing.list,
    component: AgentsBillingOrgScopedRoute,
    icon: <AccountBalance />,
    action: PERMISSIONS.VIEW_BILLING,
  },
  {
    menuTitle: MenuLabel.PromptLibrary,

    url: '/prompt_library',
    component: PromptLibraryPage,
    icon: <MenuBookOutlined />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.SipTrunkVendors,
    pageTitle: 'SIP Trunk Vendors',

    url: '/sip_trunk_vendors',
    component: SipTrunkVendorsPage,
    icon: <SettingsPhoneIcon />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.LiveKitServer,

    url: '/livekit_server',
    component: LiveKitServerPage,
    icon: <AddIcCall />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    pageTitle: 'Conversations Billing',

    url: ROUTE_PATTERN.billing.agent,
    component: ConversationsBillingOrgScopedRoute,
    action: PERMISSIONS.VIEW_BILLING,
  },
  {
    pageTitle: 'Conversation',
    url: ROUTE_PATTERN.billing.conversationDetail,
    component: ConversationBillingDetailOrgScopedRoute,
    action: PERMISSIONS.VIEW_CONVERSATIONS,
  },
  {
    menuTitle: MenuLabel.BatchCalls,
    pageTitle: 'Mass Outreach Tasks',

    url: '/batch_calls',
    component: BatchCallListPage,
    icon: <BatchCallIcon />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    pageTitle: 'Mass Outreach Tasks',

    url: '/batch_calls/task_wizard',
    component: CallTaskWizardPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    url: '/batch_calls/task_wizard/:task_id',
    component: CallTaskWizardPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    url: '/calls_management/:task_id',
    component: CallsManagementPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.Logout,

    onClick: () =>
      logout({ logoutParams: { returnTo: window.location.origin } }),
    icon: <ExitToApp />,
  },
];

export const getUnauthMenuItems = (loginWithRedirect: Function): MenuItem[] => [
  {
    menuTitle: MenuLabel.Login,
    onClick: () => loginWithRedirect(),
    icon: null,
  },
];

export const useRoutes = (
  login: Function,
  logout: Function,
  isAuthenticated: boolean
) => {
  const authMenuItems = useMemo(
    () => getAuthenticatedMenuItems(logout),
    [logout]
  );

  const unauthMenuItems = useMemo(() => getUnauthMenuItems(login), [login]);

  const menuItems = isAuthenticated ? authMenuItems : unauthMenuItems;

  const [publicRoutes, privateRoutes] = useMemo(() => {
    const itemsWithRoutes = menuItems.filter(i => i.component && i.url);

    return [
      itemsWithRoutes.filter(i => i.isPublic),
      itemsWithRoutes.filter(i => !i.isPublic),
    ];
  }, [menuItems]);

  return { publicRoutes, privateRoutes, menuItems };
};
